import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  Clock,
  Download,
  History,
  LayoutDashboard,
  Plus,
  TrendingUp,
} from "lucide-react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FinanceApi } from "api/finance/service";
import { BoQApi } from "api/boq/service";
import { Tabs, TabsList, TabsTrigger } from "components/ui-custom/Tabs";
import TransactionModal from "screens/Project Dashboard/Finance/AddNewTransaction";
import MaterialPurchaseForm from "screens/Project Dashboard/Finance/MaterialPurchaseForm";
import OtherPaymentForm from "screens/Project Dashboard/Finance/OtherPaymentForm";
import RecievedPaymentForm from "screens/Project Dashboard/Finance/RecievedPaymentForm";
import RefundForm from "screens/Project Dashboard/Finance/RefundPaymentForm";
import WorkerPayment from "screens/Project Dashboard/Workers/WorkerPayment";
import {
  ContractorPaymentModal,
  MaterialPurchaseModal,
  OtherTransactionModal,
  RefundTransactionModal,
  WorkerTransactionModal,
} from "screens/Project Dashboard/Finance/Finance";
import {
  ContractorTransactionModal as OrganizationContractorTransactionModal,
  MaterialPurchaseModal as OrganizationMaterialPurchaseModal,
  OtherTransactionModal as OrganizationOtherTransactionModal,
  PendingTransactionModal as OrganizationPendingTransactionModal,
  RefundTransactionModal as OrganizationRefundTransactionModal,
  VendorTransactionModal as OrganizationVendorTransactionModal,
  WorkerPendingModal as OrganizationWorkerPendingModal,
  WorkerTransactionModal as OrganizationWorkerTransactionModal,
} from "screens/Dashboard/Finance/Finance";
import {
  getFormattedTransactionId as getFormattedId,
  padLeadingZeroes,
} from "utils/utilityFunctions";
import RequireAccess from "components/RequireAccess";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";

const RS = "\u20B9";

const CHART_COLORS = [
  "#46237A",
  "#6B3FA0",
  "#8F6AC2",
  "#4F8EF7",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
];

const periodOptions = [
  { value: "7days", label: "7 days" },
  { value: "30days", label: "30 days" },
  { value: "6months", label: "6 months" },
  { value: "1year", label: "1 year" },
  { value: "all", label: "All time" },
];

const typeLabels = {
  general: "General",
  worker: "Worker",
  vendor_material: "Material",
  contractor_payment: "Contractor",
  vendor_advance_payment: "Vendor Advance",
  pending: "Pending",
};

const historyFilters = [
  "All",
  "General",
  "Material",
  "Worker",
  "Contractor",
  "Vendor Advance",
  "Pending",
  "Received",
  "Refund",
];

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatMoney = (value) =>
  `${RS}${toNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

const formatMoneyShort = (value) => {
  const amount = toNumber(value);
  const sign = amount < 0 ? "-" : "";
  const absolute = Math.abs(amount);

  if (absolute >= 10000000)
    return `${sign}${RS}${(absolute / 10000000).toFixed(2)}Cr`;
  if (absolute >= 100000)
    return `${sign}${RS}${(absolute / 100000).toFixed(2)}L`;
  if (absolute >= 1000) return `${sign}${RS}${(absolute / 1000).toFixed(1)}k`;
  return `${sign}${RS}${absolute.toLocaleString("en-IN")}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const getTransactionAmount = (transaction) => {
  if (transaction?.transaction_type === "vendor_material") {
    return toNumber(transaction?.paid_amount);
  }
  return toNumber(transaction?.amount ?? transaction?.paid_amount);
};

const getTransactionParty = (transaction) =>
  transaction?.vendor_name ||
  transaction?.worker_name ||
  transaction?.contractor_name ||
  transaction?.created_by ||
  "-";

const getTransactionReference = (transaction) =>
  transaction?.po_number ||
  transaction?.description ||
  transaction?.payment_type ||
  transaction?.transaction_id ||
  "-";

const getTransactionTypeLabel = (transaction) =>
  typeLabels[transaction?.transaction_type] ||
  transaction?.category ||
  "Transaction";

const getHistoryCategory = (transaction) => {
  if (transaction?.category === "Pending Transaction") return "Pending";
  if (transaction?.category === "Material Transaction") return "Material";
  if (transaction?.category === "Worker Transaction") return "Worker";
  if (transaction?.category === "Contractor Payment") return "Contractor";
  if (transaction?.category === "Vendor Advance Payment")
    return "Vendor Advance";
  if (transaction?.category === "General Transaction") return "General";
  return transaction?.category || getTransactionTypeLabel(transaction);
};

const formatBucketDate = (date) =>
  date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });

const formatBucketMonth = (date) =>
  date.toLocaleDateString("en-IN", {
    month: "short",
    year: "2-digit",
  });

const getChartPointDate = (index, length) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - (length - 1 - index));
  return date;
};

const getWeekBucketKey = (date) => {
  const start = getWeekStart(date);
  return `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
};

const getMonthBucketKey = (date) => `${date.getFullYear()}-${date.getMonth()}`;

const groupChartPointsByPeriod = (points, period) => {
  const normalizedPeriod = String(period || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  const shouldGroupWeekly =
    normalizedPeriod === "6months" && points.length > 31;
  const shouldGroupMonthly = normalizedPeriod === "1year" && points.length > 24;

  if (!shouldGroupWeekly && !shouldGroupMonthly) {
    return points.map((point, index) => ({
      ...point,
      label:
        normalizedPeriod === "6months"
          ? `W${index + 1}`
          : normalizedPeriod === "1year"
            ? `M${index + 1}`
            : point.label,
    }));
  }

  const buckets = new Map();
  points.forEach((point) => {
    const key = shouldGroupWeekly
      ? getWeekBucketKey(point.date)
      : getMonthBucketKey(point.date);
    const existing = buckets.get(key);

    if (existing) {
      existing.expenses += point.expenses;
      existing.received += point.received;
      existing.dues = point.dues;
      existing.remaining = point.remaining;
      existing.endDate = point.date;
    } else {
      buckets.set(key, {
        label: shouldGroupWeekly
          ? formatBucketDate(getWeekStart(point.date))
          : formatBucketMonth(point.date),
        expenses: point.expenses,
        received: point.received,
        dues: point.dues,
        remaining: point.remaining,
        endDate: point.date,
      });
    }
  });

  return Array.from(buckets.values()).map(({ endDate, ...bucket }) => bucket);
};

const buildChartSeries = (chartData = {}, period = "30days") => {
  const maxLength = Math.max(
    chartData?.expenses?.length || 0,
    chartData?.received?.length || 0,
    chartData?.dues?.length || 0,
    chartData?.remaining?.length || 0,
  );
  if (maxLength <= 0) return [];

  const points = Array.from({ length: maxLength }, (_, index) => ({
    label: period === "all" ? `P${index + 1}` : `D${index + 1}`,
    date: getChartPointDate(index, maxLength),
    expenses: toNumber(chartData?.expenses?.[index]),
    received: toNumber(chartData?.received?.[index]),
    dues: toNumber(chartData?.dues?.[index]),
    remaining: toNumber(chartData?.remaining?.[index]),
  }));

  return groupChartPointsByPeriod(points, period);
};

const getPercent = (summary, key) => {
  const value = toNumber(summary?.chart_data?.[key]);
  return `${Math.abs(value).toLocaleString("en-IN", {
    maximumFractionDigits: 1,
  })}%`;
};

const getExpenseCategoryColor = (category) => {
  const normalized = String(category || "").toLowerCase();

  if (normalized.includes("material")) return "#ef4444";
  if (normalized.includes("worker")) return "#f59e0b";
  return "#10b981";
};

const getLocalStorageNumber = (key) => {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) ? value : null;
};

const getProjectFinanceCategoryIds = () => ({
  material: getLocalStorageNumber("materialCategory"),
  worker: getLocalStorageNumber("workerCategory"),
  contractor: getLocalStorageNumber("contractorPaymentCategory"),
  refund: getLocalStorageNumber("refundCategory"),
  received: getLocalStorageNumber("receivedCategory"),
});

const getProjectCategory = (transaction) => {
  const categoryIds = getProjectFinanceCategoryIds();
  const categoryId = Number(transaction?.transaction_catagory_id);

  if (categoryIds.material && categoryId === categoryIds.material)
    return "Materials";
  if (categoryIds.worker && categoryId === categoryIds.worker) return "Workers";
  if (categoryIds.contractor && categoryId === categoryIds.contractor)
    return "Contractor";
  if (categoryIds.refund && categoryId === categoryIds.refund) return "Refund";
  if (categoryIds.received && categoryId === categoryIds.received)
    return "Received";
  if (transaction?.refund_purpose || transaction?.original_transaction_type)
    return "Refund";
  if (transaction?.worker_id || transaction?.worker_name) return "Workers";
  if (transaction?.work_order || transaction?.contractor_name)
    return "Contractor";
  if (
    transaction?.all_materials ||
    transaction?.po_number ||
    transaction?.challan_number ||
    transaction?.vendor_name
  ) {
    return "Materials";
  }
  return "General";
};

const getProjectTransactionAmount = (transaction) => {
  const category = getProjectCategory(transaction);
  if (category === "Materials")
    return toNumber(transaction?.paid_amount ?? transaction?.total_price);
  return toNumber(
    transaction?.amount ?? transaction?.paid_amount ?? transaction?.total_price,
  );
};

const getProjectTransactionDate = (transaction) =>
  transaction?.transaction_date ||
  transaction?.payment_date ||
  transaction?.created_on;

const getProjectTransactionMethod = (transaction) =>
  transaction?.transaction_method || transaction?.payment_mode || "-";

const getProjectTransactionDescription = (transaction) => {
  const category = getProjectCategory(transaction);
  if (category === "Materials") {
    return (
      transaction?.po_number ||
      transaction?.bill_number ||
      transaction?.challan_number ||
      transaction?.vendor_name ||
      "Material purchase"
    );
  }
  if (category === "Workers")
    return (
      transaction?.worker_name || transaction?.payment_type || "Worker payment"
    );
  if (category === "Contractor") {
    return (
      transaction?.milestone_name ||
      transaction?.contractor_name ||
      `Work order ${transaction?.work_order || ""}`.trim()
    );
  }
  if (category === "Refund")
    return (
      transaction?.refund_purpose || transaction?.transaction_type || "Refund"
    );
  return (
    transaction?.transaction_type ||
    transaction?.comments ||
    "General transaction"
  );
};

const getProjectTransactionType = (transaction) => {
  const category = getProjectCategory(transaction);
  if (category === "Materials") return "material";
  if (category === "Workers") return "worker";
  if (category === "Contractor") return "contractor";
  if (category === "Refund") return "refund";
  if (category === "Received") return "received";
  return "other";
};

const getProjectFormattedTransactionId = (transaction) => {
  const category = getProjectCategory(transaction);
  if (
    category === "Contractor" &&
    transaction?.work_order &&
    !transaction?.id
  ) {
    return `WO-${padLeadingZeroes(transaction.work_order, 3)}`;
  }
  return getFormattedId(transaction, getProjectTransactionType(transaction));
};

const isProjectIncome = (transaction) =>
  getProjectCategory(transaction) === "Received";
const isProjectRefund = (transaction) =>
  getProjectCategory(transaction) === "Refund";
const isProjectExpense = (transaction) =>
  !isProjectIncome(transaction) && !isProjectRefund(transaction);

const getWeekStart = (date) => {
  const weekStart = new Date(date);
  const day = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() - day);
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
};

const buildWeeklyBuckets = (transactions = [], weeks = 8) => {
  const today = new Date();
  const currentWeekStart = getWeekStart(today);
  const buckets = Array.from({ length: weeks }, (_, index) => {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() - (weeks - 1 - index) * 7);
    return {
      start: date,
      week: date.toLocaleDateString("en-IN", {
        month: "short",
        day: "numeric",
      }),
      out: 0,
      moneyIn: 0,
      moneyOut: 0,
      net: 0,
    };
  });

  transactions.forEach((transaction) => {
    const dateValue = getProjectTransactionDate(transaction);
    if (!dateValue) return;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return;
    const amount = getProjectTransactionAmount(transaction);
    const bucket = buckets.find((item, index) => {
      const next = buckets[index + 1]?.start;
      return date >= item.start && (!next || date < next);
    });
    if (!bucket) return;

    if (isProjectIncome(transaction)) {
      bucket.moneyIn += amount;
    } else if (isProjectExpense(transaction)) {
      bucket.out += amount;
      bucket.moneyOut -= amount;
    }
    bucket.net = bucket.moneyIn + bucket.moneyOut;
  });

  return buckets.map(({ start, ...bucket }) => bucket);
};

const getProjectCategorySummary = (transactions = []) => {
  const totals = transactions.reduce((acc, transaction) => {
    if (!isProjectExpense(transaction)) return acc;
    const category = getProjectCategory(transaction);
    acc[category] =
      (acc[category] || 0) + getProjectTransactionAmount(transaction);
    return acc;
  }, {});

  const totalExpense = Object.values(totals).reduce(
    (sum, value) => sum + toNumber(value),
    0,
  );
  const labels = ["Materials", "Workers", "Contractor", "General"];
  return labels
    .map((label) => {
      const spent = toNumber(totals[label]);
      return {
        name: label === "General" ? "General / Transport" : label,
        spent,
        percent: totalExpense > 0 ? (spent / totalExpense) * 100 : 0,
        color:
          label === "Materials"
            ? "#ef4444"
            : label === "Workers"
              ? "#f59e0b"
              : "#10b981",
      };
    })
    .filter((item) => item.spent > 0);
};

const buildProjectHistoryRows = (transactions = []) =>
  transactions.map((transaction, index) => {
    const category = getProjectCategory(transaction);
    const positive = category === "Received" || category === "Refund";
    return {
      rowId: `project-${transaction?.id || transaction?.work_order || index}-${category}`,
      site: transaction?.site_name || "Project",
      project: transaction?.site_name || "Project",
      reference: getProjectTransactionDescription(transaction),
      transactionId: getProjectFormattedTransactionId(transaction),
      historyCategory:
        category === "Materials"
          ? "Material"
          : category === "Workers"
            ? "Worker"
            : category,
      financeCategory: category,
      type:
        category === "Received"
          ? "Received"
          : transaction?.payment_status ||
            transaction?.transaction_status ||
            "pre-paid",
      party:
        transaction?.vendor_name ||
        transaction?.worker_name ||
        transaction?.contractor_name ||
        transaction?.created_by ||
        "-",
      method: getProjectTransactionMethod(transaction),
      date: formatDate(getProjectTransactionDate(transaction)),
      rawDate: toDateInputValue(getProjectTransactionDate(transaction)),
      amount: getProjectTransactionAmount(transaction),
      positive,
      originalTransaction: transaction,
    };
  });

const getPendingTransactionAmount = (transaction) =>
  toNumber(transaction?.pending_amount ?? transaction?.remaining_payment);

const getPendingTransactionCategory = (transaction) => {
  const type = String(transaction?.transaction_type || "").toLowerCase();
  if (
    type.includes("worker") ||
    transaction?.worker_id ||
    transaction?.worker_name
  ) {
    return "Worker Pending";
  }
  return "Material Pending";
};

const getPendingTransactionParty = (transaction) =>
  transaction?.vendor_name || transaction?.worker_name || "-";

const getPendingTransactionDescription = (transaction) => {
  if (getPendingTransactionCategory(transaction) === "Worker Pending") {
    return (
      [transaction?.worker_name, transaction?.worker_workname]
        .filter(Boolean)
        .join(" - ") || "Worker pending due"
    );
  }

  return (
    transaction?.description ||
    transaction?.po_number ||
    transaction?.vendor_name ||
    "Material pending due"
  );
};

const normalizeProjectPendingTransactions = (pendingData, categoryIds = {}) => {
  if (!pendingData?.status) return [];

  const pendingMaterials = Array.isArray(
    pendingData.pending_material_transactions,
  )
    ? pendingData.pending_material_transactions.map((transaction) => ({
        ...transaction,
        transaction_type: "Material",
        transaction_catagory_id:
          transaction.transaction_catagory_id || categoryIds.material,
        description:
          transaction?.description ||
          (transaction?.po_number
            ? `${transaction.vendor_name || "Vendor"} - ${transaction.po_number}`
            : `DLVRY-${padLeadingZeroes(transaction.id, 3)}`),
        pending_amount: transaction.remaining_payment,
        total_paid: transaction.paid_amount,
        total_amount: transaction.total_price,
      }))
    : [];

  const pendingWorkers = Array.isArray(pendingData.pending_worker_transactions)
    ? pendingData.pending_worker_transactions.map((transaction) => ({
        ...transaction,
        id: `worker-${transaction.worker_id}`,
        transaction_type: "Worker",
        transaction_catagory_id: categoryIds.worker,
        description: [transaction.worker_name, transaction.worker_workname]
          .filter(Boolean)
          .join(" - "),
        pending_amount: transaction.pending_amount,
        total_paid: transaction.total_paid,
        total_amount: transaction.total_amount,
        created_on: transaction.created_on || new Date().toISOString(),
      }))
    : [];

  return [...pendingMaterials, ...pendingWorkers];
};

const buildPendingChartData = (transactions = []) => {
  const grouped = transactions.reduce((acc, transaction) => {
    const name = getPendingTransactionDescription(transaction);
    const category = getPendingTransactionCategory(transaction).replace(
      " Pending",
      "",
    );
    const amount = getPendingTransactionAmount(transaction);

    if (!acc[name]) {
      acc[name] = {
        name,
        category,
        amount: 0,
        count: 0,
        value: 0,
      };
    }

    acc[name].amount += amount;
    acc[name].count += 1;
    acc[name].value += amount;
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => b.amount - a.amount);
};

const summaryCards = [
  {
    label: "Total Budget",
    value: `${RS}42.80L`,
    note: "Set at project start",
    tone: "emerald",
  },
  {
    label: "Spent To Date",
    value: `${RS}24.30L`,
    note: "56.8% of budget consumed",
    tone: "rose",
  },
  {
    label: "Pending Dues",
    value: `${RS}6.42L`,
    note: "3 vendors awaiting payment",
    tone: "amber",
  },
  {
    label: "Client Received",
    value: `${RS}25.00L`,
    note: `Next invoice: ${RS}8.00L due`,
    tone: "blue",
  },
];

const cashFlowCards = [
  {
    label: "This Month In",
    value: `${RS}0`,
    note: "No new client payment",
    tone: "emerald",
  },
  {
    label: "This Month Out",
    value: `${RS}3.84L`,
    note: "23% vs last month",
    tone: "rose",
  },
  {
    label: "Net This Month",
    value: `-${RS}3.84L`,
    note: "Funded from advance",
    tone: "amber",
  },
  {
    label: "Runway",
    value: "~6 wks",
    note: "At current burn rate",
    tone: "blue",
  },
];

const categorySpend = [
  { name: "Materials", spent: 2218000, percent: 56, color: "#ef4444" },
  { name: "Workers", spent: 752000, percent: 20, color: "#f59e0b" },
  { name: "Contractor", spent: 840000, percent: 21, color: "#10b981" },
  { name: "General / Transport", spent: 96000, percent: 3, color: "#10b981" },
];

const eightWeekOutflow = [
  { week: "Mar W3", out: 1.98 },
  { week: "Mar W4", out: 1.66 },
  { week: "Apr W1", out: 2.46 },
  { week: "Apr W2", out: 1.88 },
  { week: "Apr W3", out: 2.1 },
  { week: "Apr W4", out: 2.3 },
  { week: "May W1", out: 1.84 },
  { week: "May W2", out: 1.58 },
];

const weeklyCashFlow = [
  { week: "Feb W3", moneyIn: 0, moneyOut: -0.55, net: -0.55 },
  { week: "Feb W4", moneyIn: 25, moneyOut: -1.05, net: 24 },
  { week: "Mar W1", moneyIn: 0, moneyOut: -1.55, net: -1.55 },
  { week: "Mar W2", moneyIn: 0, moneyOut: -2.05, net: -2.05 },
  { week: "Mar W3", moneyIn: 0, moneyOut: -1.95, net: -1.95 },
  { week: "Mar W4", moneyIn: 0, moneyOut: -1.62, net: -1.62 },
  { week: "Apr W1", moneyIn: 0, moneyOut: -2.46, net: -2.46 },
  { week: "Apr W2", moneyIn: 0, moneyOut: -1.88, net: -1.88 },
  { week: "Apr W3", moneyIn: 0, moneyOut: -2.1, net: -2.1 },
  { week: "Apr W4", moneyIn: 0, moneyOut: -2.3, net: -2.3 },
  { week: "May W1", moneyIn: 0, moneyOut: -1.84, net: -1.84 },
  { week: "May W2", moneyIn: 0, moneyOut: -1.58, net: -1.58 },
];

const expenseBreakdown = [
  { name: "Materials", value: 56, amount: 2218000, color: "#f59e0b" },
  { name: "Workers", value: 20, amount: 752000, color: "#3b82f6" },
  { name: "Contractor", value: 21, amount: 840000, color: "#8b5cf6" },
  { name: "General", value: 3, amount: 96000, color: "#10b981" },
];

const forecast = [
  { week: "Now", value: 23.5, floor: 0 },
  { week: "Wk1", value: 21.6, floor: 0 },
  { week: "Wk2", value: 19.8, floor: 0 },
  { week: "Wk3", value: 17.9, floor: 0 },
  { week: "Wk4", value: 15.9, floor: 0 },
  { week: "Wk5", value: 13.8, floor: 0 },
  { week: "Wk6", value: 12.2, floor: 0 },
  { week: "Wk7", value: 20.3, floor: 0 },
  { week: "Wk8", value: 18.5, floor: 0 },
];

const phaseComparison = [
  { phase: "Site prep", actual: 1.62, estimate: 1.8, variance: -0.18 },
  { phase: "Foundation", actual: 4.98, estimate: 5.2, variance: -0.22 },
  { phase: "Structural", actual: 8.2, estimate: 7.8, variance: 0.4 },
  { phase: "Brickwork", actual: 3.8, estimate: 3.4, variance: 0.4 },
  { phase: "Plumbing", actual: 2.6, estimate: 2.8, variance: -0.2 },
  { phase: "Electrical", actual: 2.9, estimate: 3.2, variance: -0.3 },
  { phase: "Finishing", actual: 0, estimate: 5.9, variance: null },
];

const transactions = [
  {
    description: "Cement - 340 bags (OPC 53)",
    id: "L/2026/PO-E312",
    category: "Material",
    date: "18 Mar",
    amount: `${RS}1,22,400`,
  },
  {
    description: "TMT steel bars - 4.2 MT",
    id: "L/2026/PO-E318",
    category: "Material",
    date: "22 Mar",
    amount: `${RS}3,48,000`,
  },
  {
    description: "Masonry and plastering labour",
    id: "L/2026/WRK/041",
    category: "Workers",
    date: "31 Mar",
    amount: `${RS}2,10,000`,
  },
  {
    description: "Shuttering rental and fixing",
    id: "L/2026/GN/039",
    category: "General",
    date: "05 Apr",
    amount: `${RS}82,000`,
  },
  {
    description: "Ready mix concrete - 18m3",
    id: "L/2026/PO-E404",
    category: "Material",
    date: "25 Apr",
    amount: `${RS}57,600 up`,
    warning: true,
  },
];

const toneStyles = {
  emerald: "border-t-emerald-500 text-emerald-600 bg-emerald-50/50",
  rose: "border-t-rose-500 text-rose-600 bg-rose-50/50",
  amber: "border-t-amber-500 text-amber-600 bg-amber-50/50",
  blue: "border-t-blue-500 text-blue-600 bg-blue-50/50",
};

const chartAxis = {
  tick: { fill: "#64748b", fontSize: 12 },
  axisLine: false,
  tickLine: false,
};

const formatLakhs = (value) => `${RS}${Math.abs(value).toFixed(1)}L`;

const Card = ({ children, className = "" }) => (
  <section
    className={`rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}
  >
    {children}
  </section>
);

const FeaturePill = () => (
  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
    V1 Feature
  </span>
);

const ActionButton = ({ children, variant = "secondary", onClick }) => {
  const styles =
    variant === "primary"
      ? "bg-sm-primary text-white hover:bg-sm-primary/90"
      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-9 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition ${styles}`}
    >
      {children}
    </button>
  );
};

const MetricCard = ({ item }) => (
  <section
    className={`flex flex-col justify-start rounded-lg border border-slate-200 border-t-[3px] px-4 py-3.5 shadow-sm ${toneStyles[item.tone]}`}
  >
    <p className="text-xs font-bold uppercase leading-none text-slate-500">
      {item.label}
    </p>
    <p className="mt-3 text-2xl font-bold leading-none tracking-normal">
      {item.value}
    </p>
    {item.note && (
      <p className="mt-3 text-xs leading-none text-slate-500">{item.note}</p>
    )}
  </section>
);

const PageHeader = ({
  activeSection,
  scope,
  userPermissions,
  onCreateTransaction,
}) => {
  const titles = {
    overview: "Finance Overview",
    cashflow: "Cash Flow Timeline",
    phases: "Cost per Phase",
    history: "Finance History",
    pending: "Pending Transactions",
  };

  return (
    <div className="flex flex-col gap-4 px-10 pt-8 pb-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {titles[activeSection]}
        </h1>
        {/* <p className="mt-1 text-sm text-slate-500">
          {scope === "organization"
            ? "Consolidated finance across active organization sites"
            : "Project finance tracking by cash flow and construction phase"}
        </p> */}
      </div>
      <div className="flex flex-wrap gap-2">
        {/* <ActionButton>
          <Download className="h-4 w-4" />
          Export PDF
        </ActionButton>
        <ActionButton>
          <CalendarDays className="h-4 w-4" />
          Date Range
        </ActionButton> */}
        {scope === "project" &&
          (userPermissions?.edit_finance ||
            userPermissions?.is_admin ||
            userPermissions?.joined_as === "owner") && (
            <ActionButton variant="primary" onClick={onCreateTransaction}>
              <Plus className="h-4 w-4" />
              Transaction
            </ActionButton>
          )}
      </div>
    </div>
  );
};

const FinanceTabs = ({ activeSection, setActiveSection, scope }) => {
  const tabs = [
    {
      value: "overview",
      label: "Overview",
      icon: <LayoutDashboard className="h-4 w-4" />,
    },
    {
      value: "cashflow",
      label: "Cash Flow",
      icon: <TrendingUp className="h-4 w-4" />,
    },
  ];

  if (scope === "organization") {
    tabs.push({
      value: "history",
      label: "History",
      icon: <History className="h-4 w-4" />,
    });
  } else {
    tabs.push({
      value: "phases",
      label: "Cost by Phase",
      icon: <BarChart3 className="h-4 w-4" />,
    });
    tabs.push({
      value: "history",
      label: "History",
      icon: <History className="h-4 w-4" />,
    });
    tabs.push({
      value: "pending",
      label: "Pending",
      icon: <Clock className="h-4 w-4" />,
    });
  }

  return (
    <div className="flex items-center justify-between gap-3 px-10">
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="h-10 p-1">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="h-8 px-4 gap-2"
            >
              {tab.icon}
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
};

const AlertStrip = ({ children, action = "Review" }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-slate-800">
    <div className="flex min-w-0 items-center gap-3">
      <AlertTriangle className="h-5 w-5 shrink-0 text-rose-500" />
      <p className="min-w-0 font-medium">{children}</p>
    </div>
    <button type="button" className="shrink-0 text-sm font-bold text-amber-600">
      {action} -&gt;
    </button>
  </div>
);

const CategoryBars = ({ data = categorySpend }) => (
  <Card className="p-4">
    <div className="mb-5 flex items-center justify-between">
      <h2 className="text-base font-bold text-slate-950">
        Expense share by category
      </h2>
      {/* <FeaturePill /> */}
    </div>
    <div className="space-y-5">
      {data.map((item) => {
        const pct = Math.min(100, Math.round(toNumber(item.percent)));
        return (
          <div key={item.name}>
            <div className="mb-2 flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-slate-600">{item.name}</span>
              <span className="font-semibold text-slate-900">
                {formatMoney(item.spent)}
                <span className="ml-3 text-xs" style={{ color: item.color }}>
                  {pct}%
                </span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full"
                style={{ width: `${pct}%`, backgroundColor: item.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  </Card>
);

const OverviewCashChart = ({ data = eightWeekOutflow }) => (
  <Card className="p-4">
    <h2 className="mb-5 text-base font-bold text-slate-950">
      Cash flow - last 8 weeks
    </h2>
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="week" {...chartAxis} />
          <YAxis
            {...chartAxis}
            tickFormatter={(value) => `-${formatLakhs(value)}`}
          />
          <Tooltip
            formatter={(value) => [`-${formatLakhs(value)}`, "Money out"]}
          />
          <Bar dataKey="out" radius={[6, 6, 0, 0]} fill="#ef4444" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const OverviewSection = ({
  cards = summaryCards,
  categoryData = categorySpend,
  outflowData = eightWeekOutflow,
  alert = null,
}) => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((item) => (
        <MetricCard key={item.label} item={item} />
      ))}
    </div>
    {alert ? (
      <AlertStrip>{alert}</AlertStrip>
    ) : (
      <AlertStrip>
        <span className="font-bold text-rose-600">Workers budget at 94%</span> -{" "}
        {RS}
        3,200 remaining. Materials category exceeded by {RS}18,000.
      </AlertStrip>
    )}
    <div className="grid gap-5 xl:grid-cols-2">
      <CategoryBars data={categoryData} />
      <OverviewCashChart data={outflowData} />
    </div>
  </div>
);

const WeeklyCashFlowChart = ({ data = weeklyCashFlow }) => (
  <Card className="p-4">
    <div className="mb-4 flex items-center justify-between gap-4">
      <div>
        <h2 className="text-base font-bold text-slate-950">
          Weekly cash flow - last 12 weeks
        </h2>
        <p className="text-xs text-slate-500">
          Money in vs money out per week - negative weeks flagged automatically
        </p>
      </div>
      {/* <FeaturePill /> */}
    </div>
    <div className="h-[360px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid stroke="#e2e8f0" />
          <XAxis dataKey="week" {...chartAxis} />
          <YAxis {...chartAxis} tickFormatter={formatLakhs} />
          <Tooltip
            formatter={(value) => formatLakhs(value)}
            labelStyle={{ fontSize: "0.80rem" }}
            itemStyle={{ fontSize: "0.80rem" }}
          />
          <Legend iconSize={12} />
          <Bar
            dataKey="moneyIn"
            name="Money in"
            fill="#10b981"
            radius={[5, 5, 0, 0]}
          />
          <Bar
            dataKey="moneyOut"
            name="Money out"
            fill="#ef4444"
            radius={[0, 0, 5, 5]}
          />
          <Line
            type="monotone"
            dataKey="net"
            name="Net position"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const ExpenseDonut = ({ data = expenseBreakdown }) => {
  const totalAmount = data.reduce(
    (sum, entry) => sum + toNumber(entry.amount),
    0,
  );

  return (
    <Card className="p-4">
      <h2 className="mb-4 text-base font-bold text-slate-950">
        Expense category breakdown
      </h2>
      {data.length > 0 ? (
        <div className="grid min-h-[320px] items-center gap-5 lg:grid-cols-[320px_1fr]">
          <div className="relative mx-auto h-64 w-64">
            <PieChart width={280} height={280}>
              <Pie
                data={data}
                cx={125}
                cy={125}
                dataKey="value"
                nameKey="name"
                innerRadius={80}
                outerRadius={110}
                paddingAngle={2}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={
                      entry.color || CHART_COLORS[index % CHART_COLORS.length]
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name, props) => [
                  `${value}% (${formatMoney(props?.payload?.amount)})`,
                  name,
                ]}
              />
            </PieChart>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-xs text-slate-500">Total Expense</div>
              <div className="text-lg font-semibold text-slate-950">
                {formatMoney(totalAmount)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {data.map((entry, index) => (
              <div
                key={entry.name}
                className="flex items-center border-b border-slate-200 pb-2 text-sm"
              >
                <span
                  className="mr-3 h-4 w-4 rounded-full"
                  style={{
                    backgroundColor:
                      entry.color || CHART_COLORS[index % CHART_COLORS.length],
                  }}
                />
                <span className="min-w-0 flex-1 font-medium text-slate-700">
                  {entry.name}
                </span>
                <span className="w-20 text-right font-semibold text-slate-950">
                  {entry.value}%
                </span>
                <span className="ml-4 w-32 text-right text-slate-500">
                  {formatMoney(entry.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex h-[260px] items-center justify-center text-sm text-slate-500">
          No expense data found.
        </div>
      )}
    </Card>
  );
};

const ShortfallForecast = ({ data = forecast }) => (
  <Card className="p-5">
    <h2 className="mb-5 text-lg font-bold text-slate-950">
      Projected shortfall alert
    </h2>
    <AlertStrip action="Raise invoice">
      At current burn rate,{" "}
      <span className="font-bold text-rose-600">
        funds will run short in ~6 weeks
      </span>{" "}
      (Jun 21). Next client milestone invoice is {RS}8L - raise it now.
    </AlertStrip>
    <p className="mt-5 text-sm font-medium text-slate-500">
      Forecast next 8 weeks
    </p>
    <div className="mt-3 h-[230px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid stroke="#e2e8f0" />
          <XAxis dataKey="week" {...chartAxis} />
          <YAxis {...chartAxis} tickFormatter={formatLakhs} />
          <Tooltip formatter={(value) => formatLakhs(value)} />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#3b82f6"
            fill="#dbeafe"
            strokeWidth={3}
          />
          <Line
            dataKey="floor"
            stroke="#ef4444"
            strokeDasharray="4 4"
            dot={false}
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const CashFlowSection = ({
  cards = cashFlowCards,
  cashFlowData = weeklyCashFlow,
  expenseData = expenseBreakdown,
  forecastData = forecast,
}) => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((item) => (
        <MetricCard key={item.label} item={item} />
      ))}
    </div>
    <WeeklyCashFlowChart data={cashFlowData} />
    <div className="grid gap-5 xl:grid-cols-1">
      <ExpenseDonut data={expenseData} />
      {/* <ShortfallForecast data={forecastData} /> */}
    </div>
  </div>
);

const PeriodSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition hover:bg-slate-50 focus:border-purple-400"
  >
    {periodOptions.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

const OrgFinanceState = ({ loading, error, notice, children }) => {
  if (loading) {
    return (
      <Card className="p-8 text-center text-sm font-semibold text-slate-500">
        Loading finance data...
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8 text-center">
        <p className="font-bold text-rose-600">Unable to load finance data</p>
        <p className="mt-2 text-sm text-slate-500">{error}</p>
      </Card>
    );
  }

  if (notice) {
    return (
      <div className="space-y-4">
        <Card className="p-4 text-sm font-semibold text-slate-600">
          {notice}
        </Card>
        {children}
      </div>
    );
  }

  return children;
};

const OrganizationOverviewSection = ({
  summary,
  transactions,
  pendingTransactions,
  chartSeries,
  period,
  onPeriodChange,
  loading,
  error,
  notice,
}) => {
  const categoryTotals = useMemo(() => {
    const totals = transactions.reduce((acc, transaction) => {
      const category = getHistoryCategory(transaction);
      const amount = getTransactionAmount(transaction);

      if (category === "Received" || category === "Refund") return acc;

      acc[category] = (acc[category] || 0) + amount;
      return acc;
    }, {});

    return Object.entries(totals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [transactions]);

  const cards = [
    {
      label: "Total Paid",
      value: formatMoney(summary?.total_paid),
      // note: `${getPercent(summary, "expenses_percent")} change in selected period`,
      tone: "rose",
    },
    {
      label: "Total Pending",
      value: formatMoney(summary?.total_pending),
      note: `${pendingTransactions.length} pending dues`,
      tone: "amber",
    },
    {
      label: "Total Received",
      value: formatMoney(summary?.total_received),
      note: `${getPercent(summary, "received_percent")} change in selected period`,
      tone: "emerald",
    },
    {
      label: "Total Refunded",
      value: formatMoney(summary?.total_refunded),
      note: "Refunds returned against transactions",
      tone: "blue",
    },
    {
      label: "Net Remaining",
      value: formatMoney(
        toNumber(summary?.total_received) - toNumber(summary?.total_paid),
      ),
      note: `${summary?.total_transactions || transactions.length} completed transactions`,
      tone: "blue",
    },
  ];

  return (
    <OrgFinanceState loading={loading} error={error} notice={notice}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-950">
              Organization overview
            </h2>
            {/* <p className="mt-1 text-sm text-slate-500">
              Payments, receipts, refunds, and dues consolidated from active sites.
            </p> */}
          </div>
          <PeriodSelect value={period} onChange={onPeriodChange} />
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {cards.map((item) => (
            <MetricCard key={item.label} item={item} />
          ))}
        </div>

        {pendingTransactions.length > 0 && (
          <AlertStrip action="Open history">
            <span className="font-bold text-rose-600">
              {formatMoney(summary?.total_pending)} pending
            </span>{" "}
            across vendor and worker dues.
          </AlertStrip>
        )}

        <div className="grid gap-5 xl:grid-cols-2">
          <Card className="p-4">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-950">
                Payment Trend
              </h2>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartSeries}>
                  <CartesianGrid stroke="#e2e8f0" />
                  <XAxis dataKey="label" {...chartAxis} />
                  <YAxis {...chartAxis} tickFormatter={formatMoneyShort} />
                  <Tooltip formatter={(value) => formatMoney(value)} />
                  <Legend />
                  <Bar
                    dataKey="expenses"
                    name="Paid"
                    fill="#ef4444"
                    radius={[5, 5, 0, 0]}
                  />
                  <Bar
                    dataKey="received"
                    name="Received"
                    fill="#10b981"
                    radius={[5, 5, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="remaining"
                    name="Remaining"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="mb-5 text-base font-bold text-slate-950">
              Expense categories
            </h2>
            {categoryTotals.length > 0 ? (
              <div className="space-y-5">
                {categoryTotals.map((item) => {
                  const pct = toNumber(summary?.total_paid)
                    ? Math.min(
                        100,
                        (item.value / toNumber(summary?.total_paid)) * 100,
                      )
                    : 0;
                  const categoryColor = getExpenseCategoryColor(item.name);
                  return (
                    <div key={item.name}>
                      <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                        <span className="font-medium text-slate-600">
                          {item.name}
                        </span>
                        <span className="font-semibold text-slate-900">
                          {formatMoney(item.value)}
                          <span
                            className="ml-3 text-xs"
                            style={{ color: categoryColor }}
                          >
                            {pct.toFixed(1)}%
                          </span>
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: categoryColor,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-[220px] items-center justify-center text-sm text-slate-500">
                No expense categories found.
              </div>
            )}
          </Card>
        </div>
      </div>
    </OrgFinanceState>
  );
};

const OrganizationCashFlowSection = ({
  summary,
  chartSeries,
  period,
  onPeriodChange,
  loading,
  error,
  notice,
}) => {
  const net = toNumber(summary?.total_received) - toNumber(summary?.total_paid);
  const cards = [
    {
      label: "Money In",
      value: formatMoney(summary?.total_received),
      // note: `${getPercent(summary, "received_percent")} change`,
      tone: "emerald",
    },
    {
      label: "Money Out",
      value: formatMoney(summary?.total_paid),
      // note: `${getPercent(summary, "expenses_percent")} change`,
      tone: "rose",
    },
    {
      label: "Net Position",
      value: formatMoney(net),
      // note: `${getPercent(summary, "remaining_percent")} change`,
      tone: net >= 0 ? "blue" : "amber",
    },
    {
      label: "Open Dues",
      value: formatMoney(summary?.total_pending),
      // note: `${getPercent(summary, "dues_percent")} change`,
      tone: "amber",
    },
  ];

  return (
    <OrgFinanceState loading={loading} error={error} notice={notice}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Cash flow</h2>
            {/* <p className="mt-1 text-sm text-slate-500">
              Track paid, received, dues, and remaining cash position.
            </p> */}
          </div>
          <PeriodSelect value={period} onChange={onPeriodChange} />
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((item) => (
            <MetricCard key={item.label} item={item} />
          ))}
        </div>

        <Card className="p-4">
          <h2 className="mb-5 text-base font-bold text-slate-950">
            Cash flow by period
          </h2>
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartSeries}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="label" {...chartAxis} />
                <YAxis {...chartAxis} tickFormatter={formatMoneyShort} />
                <Tooltip
                  formatter={(value) => formatMoney(value)}
                  itemStyle={{ fontSize: "0.8rem" }}
                  labelStyle={{ fontSize: "0.8rem" }}
                />
                <Legend iconSize={12} />
                <Bar
                  dataKey="received"
                  name="Received"
                  fill="#10b981"
                  radius={[5, 5, 0, 0]}
                />
                <Bar
                  dataKey="expenses"
                  name="Paid"
                  fill="#ef4444"
                  radius={[5, 5, 0, 0]}
                />
                <Line
                  type="monotone"
                  dataKey="dues"
                  name="Dues"
                  stroke="#f97316"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="remaining"
                  name="Remaining"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </OrgFinanceState>
  );
};

const HistoryFilterButton = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`shrink-0 rounded-lg px-3 py-1 text-sm font-semibold transition ${
      active
        ? "bg-purple-50 text-purple-700 ring-1 ring-purple-300"
        : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
    }`}
  >
    {label}
  </button>
);

const HistorySection = ({ rows, loading, error, notice, onView }) => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedProject, setSelectedProject] = useState("All Projects");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const projectOptions = useMemo(
    () => [
      "All Projects",
      ...Array.from(
        new Set(rows.map((row) => row.project || row.site).filter(Boolean)),
      ),
    ],
    [rows],
  );

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      if (activeFilter === "All" && row.historyCategory === "Pending") {
        return false;
      }
      if (activeFilter !== "All" && row.historyCategory !== activeFilter) {
        return false;
      }
      if (
        selectedProject !== "All Projects" &&
        (row.project || row.site) !== selectedProject
      ) {
        return false;
      }
      if (fromDate && row.rawDate && row.rawDate < fromDate) {
        return false;
      }
      if (toDate && row.rawDate && row.rawDate > toDate) {
        return false;
      }
      if ((fromDate || toDate) && !row.rawDate) {
        return false;
      }
      return true;
    });
  }, [activeFilter, fromDate, rows, selectedProject, toDate]);
  const hideReferenceColumn =
    activeFilter === "Worker" || activeFilter === "Vendor Advance";
  const hideMethodColumn = activeFilter === "Pending";
  const columnCount =
    9 - (hideReferenceColumn ? 1 : 0) - (hideMethodColumn ? 1 : 0);

  return (
    <OrgFinanceState loading={loading} error={error} notice={notice}>
      <Card className="overflow-hidden p-4">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-950">
              Transaction history
            </h2>
          </div>
          <div
            className="flex max-w-full items-center px-1 py-1 gap-2 overflow-x-auto"
            style={{ scrollbarWidth: "none" }}
          >
            {historyFilters.map((filter) => (
              <HistoryFilterButton
                key={filter}
                label={filter}
                active={activeFilter === filter}
                onClick={() => setActiveFilter(filter)}
              />
            ))}
          </div>
        </div>

        <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">Category</span>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            >
              {historyFilters.map((filter) => (
                <option key={filter} value={filter}>
                  {filter}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">Project</span>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            >
              {projectOptions.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">From Date</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">To Date</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3">Site</th>
                {!hideReferenceColumn && (
                  <th className="px-3 py-3">Reference</th>
                )}
                <th className="px-3 py-3">Transaction ID</th>
                <th className="px-1 py-3">Category</th>
                <th className="px-3 py-3">Party</th>
                {!hideMethodColumn && <th className="px-3 py-3">Method</th>}
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length > 0 ? (
                visibleRows.map((row) => (
                  <tr
                    key={row.rowId}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-3 py-3 font-semibold w-[120px] text-ellipsis text-slate-900">
                      {row.site}
                    </td>
                    {!hideReferenceColumn && (
                      <td className="px-3 py-3 text-slate-600">
                        {row.reference}
                      </td>
                    )}
                    <td className="px-3 py-3 text-slate-500">
                      {row.transactionId}
                    </td>
                    <td className="px-1 py-3">
                      <span
                        className={`rounded-md px-2 py-1 text-xs font-bold ${
                          categoryClass[row.historyCategory] ||
                          "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {row.pendingCategory || row.historyCategory}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{row.party}</td>
                    {!hideMethodColumn && (
                      <td className="px-3 py-3 text-slate-500">{row.method}</td>
                    )}
                    <td className="px-3 py-3 text-slate-500">{row.date}</td>
                    <td
                      className={`px-3 py-3 text-right font-bold ${
                        row.positive ? "text-emerald-600" : "text-rose-600"
                      }`}
                    >
                      {row.positive ? "+" : "-"}
                      {formatMoney(row.amount)}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                        onClick={() => onView(row.originalTransaction)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-3 py-14 text-center text-sm text-slate-500"
                  >
                    No transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </OrgFinanceState>
  );
};

const ProjectHistorySection = ({ rows, loading, error, onView }) => (
  <ProjectHistoryContent
    rows={rows}
    loading={loading}
    error={error}
    onView={onView}
  />
);

const ProjectHistoryContent = ({ rows, loading, error, onView }) => {
  const [activeFilter, setActiveFilter] = useState("All");
  const filters = [
    "All",
    "General",
    "Material",
    "Worker",
    "Contractor",
    "Received",
    "Refund",
  ];
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      if (activeFilter !== "All" && row.historyCategory !== activeFilter) {
        return false;
      }
      if (fromDate && row.rawDate && row.rawDate < fromDate) {
        return false;
      }
      if (toDate && row.rawDate && row.rawDate > toDate) {
        return false;
      }
      if ((fromDate || toDate) && !row.rawDate) {
        return false;
      }
      return true;
    });
  }, [activeFilter, fromDate, rows, toDate]);

  return (
    <OrgFinanceState loading={loading} error={error}>
      <Card className="overflow-hidden p-4">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950">
              Transaction history
            </h2>
          </div>
          <div
            className="flex max-w-full px-1 py-1 items-center gap-2 overflow-x-auto"
            style={{ scrollbarWidth: "none" }}
          >
            {filters.map((filter) => (
              <HistoryFilterButton
                key={filter}
                label={filter}
                active={activeFilter === filter}
                onClick={() => setActiveFilter(filter)}
              />
            ))}
          </div>
        </div>
        <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">Category</span>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            >
              {filters.map((filter) => (
                <option key={filter} value={filter}>
                  {filter}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">From Date</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">To Date</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="text-left text-[#718EBF]">
                <th className="py-3 px-4 font-medium">Description</th>
                <th className="py-3 px-4 font-medium">Transaction ID</th>
                <th className="py-3 px-4 font-medium">Type</th>
                <th className="py-3 px-4 font-medium">Medium</th>
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Amount</th>
                <th className="py-3 px-4 font-medium">Receipt</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {visibleRows.length > 0 ? (
                visibleRows.map((row) => (
                  <tr
                    key={row.rowId}
                    className="border-b hover:bg-gray-100 cursor-pointer"
                  >
                    <td className="py-3 px-4">{row.reference}</td>
                    <td className="py-3 px-4">{row.transactionId}</td>
                    <td className="py-3 px-4">{row.type}</td>
                    <td className="py-3 px-4">{row.method}</td>
                    <td className="py-3 px-4">{row.date}</td>
                    <td className="py-3 px-4">
                      <span
                        className={
                          row.positive ? "text-green-600" : "text-red-600"
                        }
                      >
                        {row.positive ? "↑" : "↓"}
                        {formatMoney(row.amount)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        type="button"
                        className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                        onClick={() => onView(row.originalTransaction)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-500">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </OrgFinanceState>
  );
};

const ProjectPendingStats = ({ transactions = [] }) => {
  const chartData = buildPendingChartData(transactions);
  const totalPending = chartData.reduce(
    (sum, item) => sum + toNumber(item.amount),
    0,
  );

  if (!chartData.length) {
    return (
      <Card className="p-6 text-sm text-slate-500">
        No pending transactions available.
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="grid items-center gap-6 lg:grid-cols-[320px_1fr]">
        <div className="relative mx-auto h-64 w-64">
          <PieChart width={280} height={280}>
            <Pie
              data={chartData}
              cx={125}
              cy={125}
              startAngle={90}
              endAngle={-270}
              innerRadius={85}
              outerRadius={110}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.name}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip formatter={(value) => formatMoney(value)} />
          </PieChart>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-xs text-slate-500">Total Pending</div>
            <div className="text-xl font-semibold text-rose-500">
              {formatMoney(totalPending)}
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-4 text-lg font-medium text-slate-950">
            Pending Distribution
          </h3>
          <div className="max-h-56 space-y-3 overflow-y-auto pr-2">
            {chartData.map((item, index) => {
              const pct =
                totalPending > 0
                  ? Math.round((item.amount / totalPending) * 100)
                  : 0;
              return (
                <div
                  key={item.name}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <div className="flex min-w-0 items-center">
                    <span
                      className="mr-3 h-3 w-3 shrink-0 rounded-full"
                      style={{
                        backgroundColor:
                          CHART_COLORS[index % CHART_COLORS.length],
                      }}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-slate-800">{item.name}</p>
                      <p className="text-xs text-slate-400">{item.category}</p>
                    </div>
                  </div>
                  <span className="shrink-0 text-slate-500">
                    {item.count} transaction{item.count !== 1 ? "s" : ""}
                  </span>
                  <span className="w-14 shrink-0 text-right font-medium">
                    {pct}%
                  </span>
                  <span className="w-28 shrink-0 text-right text-rose-500">
                    {formatMoney(item.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

const ProjectPendingSection = ({ rows = [], loading, error, onView }) => (
  <OrgFinanceState loading={loading} error={error}>
    <div className="space-y-6">
      <ProjectPendingStats transactions={rows} />
      <Card className="overflow-hidden p-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-950">
            Pending transactions
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="text-left text-[#718EBF]">
                <th className="py-3 px-4 font-medium">Transaction Type</th>
                <th className="py-3 px-4 font-medium">Description</th>
                <th className="py-3 px-4 font-medium">Pending Amount</th>
                <th className="py-3 px-4 font-medium">Paid Amount</th>
                <th className="py-3 px-4 font-medium">Total Amount</th>
                <th className="py-3 px-4 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {rows.length > 0 ? (
                rows.map((transaction, index) => (
                  <tr
                    key={`${transaction?.id || transaction?.worker_id || index}-pending`}
                    className="border-b hover:bg-gray-100"
                  >
                    <td className="py-3 px-4">
                      <span
                        className={`rounded-md px-2 py-1 text-xs font-bold ${
                          getPendingTransactionCategory(transaction) ===
                          "Worker Pending"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {getPendingTransactionCategory(transaction).replace(
                          " Pending",
                          "",
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800">
                        {getPendingTransactionDescription(transaction)}
                      </div>
                      <div className="text-xs text-slate-400">
                        {getPendingTransactionParty(transaction)}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-rose-500">
                      {formatMoney(getPendingTransactionAmount(transaction))}
                    </td>
                    <td className="py-3 px-4">
                      {formatMoney(transaction?.total_paid)}
                    </td>
                    <td className="py-3 px-4">
                      {formatMoney(transaction?.total_amount)}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        type="button"
                        className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                        onClick={() => onView(transaction)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-500">
                    No pending transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  </OrgFinanceState>
);

const PendingTransactionDetailsModal = ({ transaction, onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-950">
          Pending Transaction
        </h2>
        <button
          type="button"
          className="rounded-lg px-3 py-1 text-sm font-semibold text-slate-500 hover:bg-slate-100"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className="space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Category</span>
          <span className="font-semibold text-slate-900">
            {getPendingTransactionCategory(transaction)}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Party</span>
          <span className="font-semibold text-slate-900">
            {getPendingTransactionParty(transaction)}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Description</span>
          <span className="text-right font-semibold text-slate-900">
            {getPendingTransactionDescription(transaction)}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Pending Amount</span>
          <span className="font-bold text-rose-500">
            {formatMoney(getPendingTransactionAmount(transaction))}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Paid Amount</span>
          <span className="font-semibold text-slate-900">
            {formatMoney(transaction?.total_paid)}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Total Amount</span>
          <span className="font-semibold text-slate-900">
            {formatMoney(transaction?.total_amount)}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const PhaseList = ({ data = phaseComparison }) => (
  <Card className="p-4">
    <div className="mb-5 flex items-center justify-between">
      <h2 className="text-base font-bold text-slate-950">
        Phase-wise cost comparison
      </h2>
      <div className="flex items-center gap-3 text-sm font-semibold">
        <span className="text-blue-600">Actual</span>
        <span className="text-amber-500">| Estimate</span>
      </div>
    </div>
    <div className="space-y-5">
      {data.map((item) => {
        const actualPct = item.estimate
          ? Math.min(100, (item.actual / item.estimate) * 100)
          : 0;
        const over = item.variance > 0;
        return (
          <div
            key={item.phase}
            className="grid items-center gap-3 text-sm sm:grid-cols-[150px_1fr_130px]"
          >
            <span className="font-medium text-slate-600">{item.phase}</span>
            <div className="relative h-3 rounded-full bg-slate-100">
              <div
                className={`h-3 rounded-full ${over ? "bg-rose-500" : "bg-blue-500"}`}
                style={{ width: `${actualPct}%` }}
              />
              <span
                className="absolute top-1/2 h-7 w-0.5 -translate-y-1/2 bg-amber-500"
                style={{ left: `${Math.min(100, item.estimate ? 100 : 0)}%` }}
              />
            </div>
            <span className="text-right font-semibold text-slate-700">
              {item.actual ? `${RS}${item.actual.toFixed(2)}L` : "-"}
              {item.variance === null ? (
                <span className="ml-2 font-normal text-slate-400">
                  not started
                </span>
              ) : (
                <span
                  className={`ml-2 ${over ? "text-rose-600" : "text-emerald-600"}`}
                >
                  {over ? "+" : "-"}
                  {RS}
                  {Math.abs(item.variance * 100000).toLocaleString("en-IN")}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  </Card>
);

const PhaseChart = ({ data = phaseComparison }) => (
  <Card className="p-4">
    <h2 className="mb-5 text-base font-bold text-slate-950">
      Phase cost breakdown
    </h2>
    <div className="h-[330px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid stroke="#e2e8f0" />
          <XAxis
            dataKey="phase"
            {...chartAxis}
            angle={-15}
            textAnchor="end"
            height={70}
          />
          <YAxis {...chartAxis} tickFormatter={formatLakhs} />
          <Tooltip
            formatter={(value) => formatLakhs(value)}
            itemStyle={{ fontSize: "0.875rem" }}
            labelStyle={{ fontSize: "0.875rem" }}
          />
          <Legend iconSize={12} />
          <Bar
            dataKey="estimate"
            name="Estimate"
            fill="#1e3a8a"
            radius={[5, 5, 0, 0]}
          />
          <Bar dataKey="actual" name="Actual" radius={[5, 5, 0, 0]}>
            {data.map((entry) => (
              <Cell
                key={entry.phase}
                fill={entry.variance > 0 ? "#ef4444" : "#3b82f6"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const categoryClass = {
  Material: "bg-amber-100 text-amber-700",
  Workers: "bg-blue-100 text-blue-700",
  Worker: "bg-blue-100 text-blue-700",
  General: "bg-emerald-100 text-emerald-700",
  Contractor: "bg-violet-100 text-violet-700",
  "Vendor Advance": "bg-indigo-100 text-indigo-700",
  Pending: "bg-orange-100 text-orange-700",
  Received: "bg-emerald-100 text-emerald-700",
  Refund: "bg-sky-100 text-sky-700",
};

const TransactionsTable = ({
  sections = [],
  selectedSectionId,
  onSelectSection,
  rows = transactions,
  loading = false,
}) => (
  <Card className="overflow-hidden p-4">
    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <h2 className="text-base font-bold text-slate-950">
        Detailed phase transactions
      </h2>
      {sections.length > 0 && (
        <div
          className="flex max-w-full items-center py-1 px-1 gap-2 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => onSelectSection(section.id)}
              className={`h-8 shrink-0 rounded-lg px-3 text-sm font-semibold transition ${
                selectedSectionId === section.id
                  ? "bg-purple-50 text-purple-700 ring-1 ring-purple-300"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {section.phase || section.name}
            </button>
          ))}
        </div>
      )}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-3">Description</th>
            <th className="px-3 py-3">Transaction ID</th>
            <th className="px-3 py-3">Category</th>
            <th className="px-3 py-3">Date</th>
            <th className="px-3 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={5}
                className="px-3 py-12 text-center text-sm text-slate-500"
              >
                Loading transactions...
              </td>
            </tr>
          ) : rows.length > 0 ? (
            rows.map((row) => (
              <tr
                key={row.rowId || row.id}
                className={`border-b border-slate-100 ${row.warning ? "bg-rose-50" : "hover:bg-slate-50"}`}
              >
                <td className="px-3 py-4 font-semibold text-slate-900">
                  {row.description}
                </td>
                <td className="px-3 py-4 text-slate-500">{row.id}</td>
                <td className="px-3 py-4">
                  <span
                    className={`rounded-md px-2 py-1 text-xs font-bold ${categoryClass[row.category] || "bg-slate-100 text-slate-700"}`}
                  >
                    {row.category}
                  </span>
                </td>
                <td className="px-3 py-4 text-slate-500">{row.date}</td>
                <td
                  className={`px-3 py-4 text-right font-bold ${row.warning ? "text-rose-600" : "text-slate-900"}`}
                >
                  {row.amount}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={5}
                className="px-3 py-12 text-center text-sm text-slate-500"
              >
                No transactions found for this section.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

const PhaseSection = ({ sections = [], data = phaseComparison }) => {
  const [selectedSectionId, setSelectedSectionId] = useState(
    sections[0]?.id || null,
  );
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!selectedSectionId && sections[0]?.id) {
      setSelectedSectionId(sections[0].id);
    }
  }, [selectedSectionId, sections]);

  useEffect(() => {
    const selectedSection = sections.find(
      (section) => section.id === selectedSectionId,
    );
    if (!selectedSection) {
      setHistoryRows([]);
      return;
    }

    let isCancelled = false;
    const fetchSectionHistory = async () => {
      try {
        setHistoryLoading(true);
        const histories = await Promise.all(
          (selectedSection.items || []).map(async (item) => {
            const itemHistory = await BoQApi.getItemHistory(item.id);
            return itemHistory.map((entry, index) => ({
              rowId: `${item.id}-${entry.type || "transaction"}-${entry.id || index}`,
              id: getFormattedId(
                entry,
                entry.type === "workorder"
                  ? "contractor"
                  : entry.type === "material"
                    ? "material"
                    : entry.type === "worker"
                      ? "worker"
                      : null,
              ),
              description:
                entry.material_name ||
                entry.worker_name ||
                entry.contractor_name ||
                entry.transaction_type ||
                item.item_name ||
                "Transaction",
              category:
                entry.type === "workorder"
                  ? "Contractor"
                  : entry.type === "material"
                    ? "Material"
                    : entry.type === "worker"
                      ? "Worker"
                      : "General",
              date: formatDate(entry.payment_date || entry.created_on),
              amount: formatMoney(entry.amount),
              warning: false,
            }));
          }),
        );

        if (!isCancelled) {
          setHistoryRows(histories.flat());
        }
      } catch (_) {
        if (!isCancelled) setHistoryRows([]);
      } finally {
        if (!isCancelled) setHistoryLoading(false);
      }
    };

    fetchSectionHistory();

    return () => {
      isCancelled = true;
    };
  }, [selectedSectionId, sections]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 px-2">
        <div>
          <h2 className="text-lg font-bold text-slate-950">
            Cost per Task / Phase
          </h2>
          <p className="text-xs text-slate-500">
            Actual vs estimated cost per construction phase - feeds BoQ
            comparison
          </p>
        </div>
        {/* <FeaturePill /> */}
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <PhaseList data={data} />
        <PhaseChart data={data} />
      </div>
      <TransactionsTable
        sections={data}
        selectedSectionId={selectedSectionId}
        onSelectSection={setSelectedSectionId}
        rows={historyRows}
        loading={historyLoading}
      />
    </div>
  );
};

const NewFinanceStaticDashboard = ({
  scope = "project",
  currentMember,
  permissionsLoaded,
  userPermissions,
  permissionsLoading,
}) => {
  const [activeSection, setActiveSection] = useState("overview");
  const [period, setPeriod] = useState("30days");
  const [financePayload, setFinancePayload] = useState(null);
  const [loading, setLoading] = useState(scope === "organization");
  const [error, setError] = useState("");
  const [projectTransactions, setProjectTransactions] = useState([]);
  const [projectPendingTransactions, setProjectPendingTransactions] = useState(
    [],
  );
  const [projectBoqSections, setProjectBoqSections] = useState([]);
  const [projectLoading, setProjectLoading] = useState(
    scope !== "organization",
  );
  const [projectError, setProjectError] = useState("");
  const [isTransactionMenuOpen, setIsTransactionMenuOpen] = useState(false);
  const [createTransactionType, setCreateTransactionType] = useState(null);
  const [selectedOrganizationTransaction, setSelectedOrganizationTransaction] =
    useState(null);
  const [selectedProjectTransaction, setSelectedProjectTransaction] =
    useState(null);
  const [
    selectedProjectPendingTransaction,
    setSelectedProjectPendingTransaction,
  ] = useState(null);

  const organization = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("organization") || "{}");
    } catch (_) {
      return {};
    }
  }, []);

  const organizationId = organization?.id;
  const siteId = Number(localStorage.getItem("site"));

  const fetchOrganizationFinance = useCallback(async () => {
    if (scope !== "organization") return;

    if (!organizationId) {
      setFinancePayload(null);
      setError("No organization selected.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await FinanceApi.listTransactionsByOrganization(
        organizationId,
        period,
      );
      setFinancePayload(response || {});
    } catch (err) {
      setError(err?.message || "Failed to load organization transactions.");
      setFinancePayload(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, period, scope]);

  useEffect(() => {
    fetchOrganizationFinance();
  }, [fetchOrganizationFinance]);

  const fetchProjectFinance = useCallback(async () => {
    if (scope === "organization") return;

    if (!siteId) {
      setProjectError("No site selected.");
      setProjectLoading(false);
      return;
    }

    try {
      setProjectLoading(true);
      setProjectError("");
      const [transactionsResponse, pendingResponse, sectionsWithItems] =
        await Promise.all([
          FinanceApi.listAllFinancebySite({ sites: siteId }),
          FinanceApi.listPendingTransactionsBySite({ sites: siteId }),
          BoQApi.getSectionsWithItems(siteId),
        ]);

      if (transactionsResponse?.status === false) {
        throw new Error(
          transactionsResponse?.message || "Failed to load site transactions.",
        );
      }

      setProjectTransactions(
        Array.isArray(transactionsResponse?.data)
          ? transactionsResponse.data
          : [],
      );
      setProjectPendingTransactions(
        normalizeProjectPendingTransactions(
          pendingResponse,
          getProjectFinanceCategoryIds(),
        ),
      );
      setProjectBoqSections(
        Array.isArray(sectionsWithItems) ? sectionsWithItems : [],
      );
    } catch (err) {
      setProjectError(err?.message || "Failed to load project finance data.");
      setProjectTransactions([]);
      setProjectPendingTransactions([]);
      setProjectBoqSections([]);
    } finally {
      setProjectLoading(false);
    }
  }, [scope, siteId]);

  useEffect(() => {
    fetchProjectFinance();
  }, [fetchProjectFinance]);

  useEffect(() => {
    if (scope === "organization" && activeSection === "phases") {
      setActiveSection("overview");
    }
  }, [activeSection, scope]);

  const paymentSummary = financePayload?.payment_summary || {};
  const financeNotice =
    financePayload?.message && !financePayload?.payment_summary
      ? financePayload.message
      : "";
  const completedTransactions = useMemo(
    () => (Array.isArray(financePayload?.data) ? financePayload.data : []),
    [financePayload?.data],
  );
  const pendingTransactions = useMemo(
    () =>
      Array.isArray(financePayload?.pending_transactions)
        ? financePayload.pending_transactions
        : [],
    [financePayload?.pending_transactions],
  );
  const chartSeries = useMemo(
    () => buildChartSeries(paymentSummary?.chart_data, period),
    [paymentSummary?.chart_data, period],
  );
  const historyRows = useMemo(() => {
    const completedRows = completedTransactions.map((transaction, index) => {
      const historyCategory = getHistoryCategory(transaction);

      return {
        rowId: `completed-${transaction?.transaction_type || "transaction"}-${transaction?.id || transaction?.transaction_id || index}`,
        site: transaction?.site_name || "Organization",
        project: transaction?.site_name || "Organization",
        reference: getTransactionReference(transaction),
        transactionId: transaction?.transaction_id || "-",
        historyCategory,
        financeCategory: historyCategory,
        party: getTransactionParty(transaction),
        method: transaction?.method || "-",
        date: formatDate(
          transaction?.transaction_date || transaction?.created_on,
        ),
        rawDate: toDateInputValue(
          transaction?.transaction_date || transaction?.created_on,
        ),
        amount: getTransactionAmount(transaction),
        positive:
          historyCategory === "Received" || historyCategory === "Refund",
        originalTransaction: transaction,
      };
    });

    const pendingRows = pendingTransactions.map((transaction, index) => ({
      rowId: `pending-${transaction?.transaction_type || "due"}-${transaction?.id || transaction?.worker_id || transaction?.transaction_id || index}`,
      site: transaction?.site_name || "Organization",
      project: transaction?.site_name || "Organization",
      reference:
        transaction?.po_number || transaction?.worker_workname || "Pending due",
      transactionId: transaction?.transaction_id || "-",
      historyCategory: "Pending",
      pendingCategory: getPendingTransactionCategory(transaction),
      financeCategory: getPendingTransactionCategory(transaction),
      party: getTransactionParty(transaction),
      method: "-",
      date: formatDate(transaction?.created_on),
      rawDate: toDateInputValue(transaction?.created_on),
      amount: toNumber(
        transaction?.remaining_payment || transaction?.pending_amount,
      ),
      positive: false,
      originalTransaction: transaction,
    }));

    return [...pendingRows, ...completedRows];
  }, [completedTransactions, pendingTransactions]);
  const projectTotals = useMemo(() => {
    const totalPaid = projectTransactions
      .filter(isProjectExpense)
      .reduce(
        (sum, transaction) => sum + getProjectTransactionAmount(transaction),
        0,
      );
    const totalReceived = projectTransactions
      .filter(isProjectIncome)
      .reduce(
        (sum, transaction) => sum + getProjectTransactionAmount(transaction),
        0,
      );
    const totalRefunded = projectTransactions
      .filter(isProjectRefund)
      .reduce(
        (sum, transaction) => sum + getProjectTransactionAmount(transaction),
        0,
      );
    const totalBudget = projectBoqSections.reduce(
      (sectionSum, section) =>
        sectionSum +
        (section.items || []).reduce(
          (itemSum, item) =>
            itemSum +
            toNumber(item?.material_estimation) +
            toNumber(item?.labor_estimation),
          0,
        ),
      0,
    );

    return {
      totalPaid: totalPaid - totalRefunded,
      totalReceived,
      totalRefunded,
      totalBudget,
      totalRemaining: totalReceived - totalPaid + totalRefunded,
    };
  }, [projectBoqSections, projectTransactions]);
  const projectCategoryData = useMemo(
    () => getProjectCategorySummary(projectTransactions),
    [projectTransactions],
  );
  const projectOutflowData = useMemo(
    () =>
      buildWeeklyBuckets(projectTransactions, 8).map((bucket) => ({
        week: bucket.week,
        out: bucket.out / 100000,
      })),
    [projectTransactions],
  );
  const projectCashFlowData = useMemo(
    () =>
      buildWeeklyBuckets(projectTransactions, 12).map((bucket) => ({
        week: bucket.week,
        moneyIn: bucket.moneyIn / 100000,
        moneyOut: bucket.moneyOut / 100000,
        net: bucket.net / 100000,
      })),
    [projectTransactions],
  );
  const projectExpenseBreakdown = useMemo(() => {
    const total = projectCategoryData.reduce(
      (sum, item) => sum + item.spent,
      0,
    );
    return projectCategoryData.map((item) => ({
      name: item.name,
      value: total > 0 ? Math.round((item.spent / total) * 100) : 0,
      amount: item.spent,
      color: item.color,
    }));
  }, [projectCategoryData]);
  const projectForecastData = useMemo(() => {
    let running = projectTotals.totalReceived - projectTotals.totalPaid;
    return Array.from({ length: 8 }, (_, index) => {
      if (index > 0)
        running -=
          projectTotals.totalPaid /
          Math.max(8, projectTransactions.length || 8);
      return {
        week: index === 0 ? "Now" : `Wk${index}`,
        value: running / 100000,
        floor: 0,
      };
    });
  }, [
    projectTotals.totalPaid,
    projectTotals.totalReceived,
    projectTransactions.length,
  ]);
  const projectOverviewCards = useMemo(
    () => [
      {
        label: "Total Budget",
        value: formatMoney(projectTotals.totalBudget),
        note: "From BOQ section estimates",
        tone: "emerald",
      },
      {
        label: "Spent To Date",
        value: formatMoney(projectTotals.totalPaid),
        note: projectTotals.totalBudget
          ? `${((projectTotals.totalPaid / projectTotals.totalBudget) * 100).toFixed(1)}% of budget consumed`
          : "No BOQ budget found",
        tone: "rose",
      },
      {
        label: "Refunded",
        value: formatMoney(projectTotals.totalRefunded),
        note: "Refund transactions recorded",
        tone: "amber",
      },
      {
        label: "Client Received",
        value: formatMoney(projectTotals.totalReceived),
        note: `Net remaining: ${formatMoney(projectTotals.totalRemaining)}`,
        tone: "blue",
      },
    ],
    [projectTotals],
  );
  const projectCashFlowCards = useMemo(
    () => [
      {
        label: "Money In",
        value: formatMoney(projectTotals.totalReceived),
        note: "Received transactions",
        tone: "emerald",
      },
      {
        label: "Money Out",
        value: formatMoney(projectTotals.totalPaid),
        note: "Site expenses",
        tone: "rose",
      },
      {
        label: "Net Position",
        value: formatMoney(projectTotals.totalRemaining),
        note: "Received minus paid",
        tone: projectTotals.totalRemaining >= 0 ? "blue" : "amber",
      },
      {
        label: "Transactions",
        value: String(projectTransactions.length),
        note: "Fetched for this site",
        tone: "blue",
      },
    ],
    [projectTotals, projectTransactions.length],
  );
  const projectPhaseData = useMemo(
    () =>
      projectBoqSections.map((section) => {
        const estimateAmount = (section.items || []).reduce(
          (sum, item) =>
            sum +
            toNumber(item?.material_estimation) +
            toNumber(item?.labor_estimation),
          0,
        );
        const actualAmount = (section.items || []).reduce(
          (sum, item) =>
            sum +
            toNumber(item?.Actual_material) +
            toNumber(item?.Actual_labor),
          0,
        );

        return {
          id: section.id,
          phase:
            section.section_name || section.name || `Section ${section.id}`,
          actual: actualAmount / 100000,
          estimate: estimateAmount / 100000,
          variance: (actualAmount - estimateAmount) / 100000,
          items: section.items || [],
        };
      }),
    [projectBoqSections],
  );
  const projectHistoryRows = useMemo(
    () => buildProjectHistoryRows(projectTransactions),
    [projectTransactions],
  );
  const closeCreateTransactionSidebar = useCallback(() => {
    setCreateTransactionType(null);
    fetchProjectFinance();
  }, [fetchProjectFinance]);
  const handleCreateTransactionSelect = useCallback((transactionType) => {
    setIsTransactionMenuOpen(false);
    setCreateTransactionType(transactionType);
  }, []);

  if (
    scope === "organization" &&
    currentMember?.finance_control === false &&
    currentMember?.joined_as !== "owner" &&
    currentMember?.is_admin === false &&
    permissionsLoaded
  ) {
    return (
      <RequireAccess
        module="Finance"
        currentMember={currentMember}
        permission="finance_control"
      />
    );
  }

  if (
    scope === "project" &&
    !userPermissions?.view_finance &&
    !userPermissions?.is_admin &&
    !userPermissions?.view_all &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module={"Finance"}
        description={
          "You don't have permission to view financial information for this site. Please contact your administrator for access. "
        }
      />
    );
  }

  return (
    <div className="min-h-full text-slate-950">
      <PageHeader
        activeSection={activeSection}
        scope={scope}
        userPermissions={userPermissions}
        onCreateTransaction={
          scope === "organization"
            ? undefined
            : () => setIsTransactionMenuOpen(true)
        }
      />
      <FinanceTabs
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        scope={scope}
      />
      <div className="px-10 pb-8 pt-4">
        {scope === "organization" && activeSection === "overview" && (
          <OrganizationOverviewSection
            summary={paymentSummary}
            transactions={completedTransactions}
            pendingTransactions={pendingTransactions}
            chartSeries={chartSeries}
            period={period}
            onPeriodChange={setPeriod}
            loading={loading}
            error={error}
            notice={financeNotice}
          />
        )}
        {scope === "organization" && activeSection === "cashflow" && (
          <OrganizationCashFlowSection
            summary={paymentSummary}
            chartSeries={chartSeries}
            period={period}
            onPeriodChange={setPeriod}
            loading={loading}
            error={error}
            notice={financeNotice}
          />
        )}
        {scope === "organization" && activeSection === "history" && (
          <HistorySection
            rows={historyRows}
            loading={loading}
            error={error}
            notice={financeNotice}
            onView={setSelectedOrganizationTransaction}
          />
        )}
        {scope !== "organization" && activeSection === "overview" && (
          <OrgFinanceState loading={projectLoading} error={projectError}>
            <OverviewSection
              cards={projectOverviewCards}
              categoryData={projectCategoryData}
              outflowData={projectOutflowData}
              alert={
                <>
                  <span className="font-bold text-rose-600">
                    {formatMoney(projectTotals.totalPaid)} spent
                  </span>{" "}
                  against {formatMoney(projectTotals.totalBudget)} BOQ estimate.
                </>
              }
            />
          </OrgFinanceState>
        )}
        {scope !== "organization" && activeSection === "cashflow" && (
          <OrgFinanceState loading={projectLoading} error={projectError}>
            <CashFlowSection
              cards={projectCashFlowCards}
              cashFlowData={projectCashFlowData}
              expenseData={projectExpenseBreakdown}
              forecastData={projectForecastData}
            />
          </OrgFinanceState>
        )}
        {scope !== "organization" && activeSection === "phases" && (
          <OrgFinanceState loading={projectLoading} error={projectError}>
            <PhaseSection sections={projectPhaseData} data={projectPhaseData} />
          </OrgFinanceState>
        )}
        {scope !== "organization" && activeSection === "history" && (
          <ProjectHistorySection
            rows={projectHistoryRows}
            loading={projectLoading}
            error={projectError}
            onView={setSelectedProjectTransaction}
          />
        )}
        {scope !== "organization" && activeSection === "pending" && (
          <ProjectPendingSection
            rows={projectPendingTransactions}
            loading={projectLoading}
            error={projectError}
            onView={setSelectedProjectPendingTransaction}
          />
        )}
      </div>
      {selectedOrganizationTransaction
        ? (() => {
            const category = getHistoryCategory(
              selectedOrganizationTransaction,
            );
            const closeOrganizationTransaction = () =>
              setSelectedOrganizationTransaction(null);

            if (category === "Pending") {
              if (
                selectedOrganizationTransaction?.transaction_type === "worker"
              ) {
                return (
                  <OrganizationWorkerPendingModal
                    transaction={selectedOrganizationTransaction}
                    onClose={closeOrganizationTransaction}
                  />
                );
              }

              return (
                <OrganizationPendingTransactionModal
                  transaction={selectedOrganizationTransaction}
                  onClose={closeOrganizationTransaction}
                />
              );
            }

            if (category === "Material") {
              return (
                <OrganizationMaterialPurchaseModal
                  transaction={selectedOrganizationTransaction}
                  onClose={closeOrganizationTransaction}
                />
              );
            }

            if (category === "Worker") {
              return (
                <OrganizationWorkerTransactionModal
                  transaction={selectedOrganizationTransaction}
                  onClose={closeOrganizationTransaction}
                />
              );
            }

            if (category === "Refund") {
              return (
                <OrganizationRefundTransactionModal
                  transaction={selectedOrganizationTransaction}
                  onClose={closeOrganizationTransaction}
                />
              );
            }

            if (category === "Vendor Advance") {
              return (
                <OrganizationVendorTransactionModal
                  transaction={selectedOrganizationTransaction}
                  onClose={closeOrganizationTransaction}
                  onUpdate={fetchOrganizationFinance}
                />
              );
            }

            if (category === "Contractor") {
              return (
                <OrganizationContractorTransactionModal
                  transaction={selectedOrganizationTransaction}
                  onClose={closeOrganizationTransaction}
                  onUpdate={fetchOrganizationFinance}
                />
              );
            }

            return (
              <OrganizationOtherTransactionModal
                transaction={selectedOrganizationTransaction}
                onClose={closeOrganizationTransaction}
                onUpdate={fetchOrganizationFinance}
                isReceivedTransaction={category === "Received"}
              />
            );
          })()
        : null}
      {selectedProjectTransaction
        ? (() => {
            const category = getProjectCategory(selectedProjectTransaction);
            const closeProjectTransaction = () =>
              setSelectedProjectTransaction(null);

            if (category === "Materials") {
              return (
                <MaterialPurchaseModal
                  transaction={selectedProjectTransaction}
                  onClose={closeProjectTransaction}
                  setShowMaterialPurchaseDetails={() => {}}
                  setDelivery={() => {}}
                  canEditFinance={false}
                />
              );
            }

            if (category === "Workers") {
              return (
                <WorkerTransactionModal
                  transaction={selectedProjectTransaction}
                  onClose={closeProjectTransaction}
                  canEditFinance={false}
                  onUpdate={fetchProjectFinance}
                />
              );
            }

            if (category === "Refund") {
              return (
                <RefundTransactionModal
                  transaction={selectedProjectTransaction}
                  onClose={closeProjectTransaction}
                />
              );
            }

            if (category === "Contractor") {
              return (
                <ContractorPaymentModal
                  transaction={selectedProjectTransaction}
                  onClose={closeProjectTransaction}
                />
              );
            }

            return (
              <OtherTransactionModal
                transaction={selectedProjectTransaction}
                onClose={closeProjectTransaction}
                onUpdate={fetchProjectFinance}
                isReceivedTransaction={category === "Received"}
                canEditFinance={false}
              />
            );
          })()
        : null}
      {selectedProjectPendingTransaction ? (
        getPendingTransactionCategory(selectedProjectPendingTransaction) ===
        "Material Pending" ? (
          <MaterialPurchaseModal
            transaction={selectedProjectPendingTransaction}
            onClose={() => setSelectedProjectPendingTransaction(null)}
            setShowMaterialPurchaseDetails={() => {}}
            setDelivery={() => {}}
            isPendingView
            canEditFinance={false}
          />
        ) : (
          <PendingTransactionDetailsModal
            transaction={selectedProjectPendingTransaction}
            onClose={() => setSelectedProjectPendingTransaction(null)}
          />
        )
      ) : null}
      {isTransactionMenuOpen && scope !== "organization" ? (
        <TransactionModal
          isOpen={isTransactionMenuOpen}
          onClose={() => setIsTransactionMenuOpen(false)}
          handleClick={handleCreateTransactionSelect}
          setTab={() => {}}
          mode="modal"
        />
      ) : null}
      {createTransactionType ? (
        <div className="fixed inset-0 z-[65]">
          <div
            className="absolute inset-0 bg-black/20"
            onClick={closeCreateTransactionSidebar}
          />
          <div className="absolute right-0 top-0 h-full w-full sm:max-w-xl bg-white shadow-2xl rounded-l-2xl overflow-hidden border-l border-gray-200">
            <div className="h-full overflow-y-auto p-4">
              {createTransactionType === "material" ? (
                <MaterialPurchaseForm onClose={closeCreateTransactionSidebar} />
              ) : null}
              {createTransactionType === "worker" ? (
                <WorkerPayment
                  onClose={closeCreateTransactionSidebar}
                  setTab={() => {}}
                />
              ) : null}
              {createTransactionType === "refund" ? (
                <RefundForm onClose={closeCreateTransactionSidebar} />
              ) : null}
              {createTransactionType === "other" ? (
                <OtherPaymentForm onClose={closeCreateTransactionSidebar} />
              ) : null}
              {createTransactionType === "receive" ? (
                <RecievedPaymentForm onClose={closeCreateTransactionSidebar} />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NewFinanceStaticDashboard;
